import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Space, Tag, Row, Col, Statistic, Empty, Select } from 'antd';
import { ReloadOutlined, ShopOutlined, ShoppingCartOutlined, BarChartOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { apiFetch, onDataChanged } from '../utils/api.js';
import { yuan } from '../utils/format.js';

const DIM_META = {
  '商品': { icon: <ShoppingCartOutlined />, color: 'var(--brand)' },
  '推广': { icon: <BarChartOutlined />, color: '#1677ff' },
  '售后': { icon: <CustomerServiceOutlined />, color: '#faad14' },
  '订单发货': { icon: <ShopOutlined />, color: '#52c41a' },
};

export default function StoreData() {
  const [data, setData] = useState({});       // { 店铺名: { 维度: {data, updated_at} } }
  const [shops, setShops] = useState([]);      // 店铺名列表
  const [selectedShop, setSelectedShop] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    apiFetch('/api/collector/data').then(r => r.json()).then(d => {
      setData(d.data || {});
      setShops(d.shops || []);
      // 保持当前选中（若仍存在），否则默认第一个店铺
      setSelectedShop(prev => (prev && (d.shops || []).includes(prev)) ? prev : ((d.shops || [])[0] || null));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => onDataChanged(() => { loadData(true); }), [loadData]);

  const current = (selectedShop && data[selectedShop]) ? data[selectedShop] : {};
  const dims = Object.keys(current).filter(k => k !== '测试' && current[k] && current[k].data);

  // 商品表格列
  const goodsColumns = [
    { title: '商品图', dataIndex: 'thumb_url', key: 'thumb', width: 70, render: (u) => u ? <img src={u} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} /> : '-' },
    { title: '商品名称', dataIndex: 'goods_name', key: 'name', ellipsis: true },
    { title: '商品编号', dataIndex: 'goods_id', key: 'id', width: 130 },
    { title: '类目', dataIndex: 'template_info_vo', key: 'cat', width: 160, render: (t) => (t && t.name) || '-' },
  ];

  // 订单表格列
  const orderColumns = [
    { title: '订单号', dataIndex: 'order_sn', key: 'sn', width: 180, ellipsis: true },
    { title: '商品', dataIndex: 'goods_name', key: 'goods', ellipsis: true },
    { title: '规格', dataIndex: 'spec', key: 'spec', width: 110, ellipsis: true },
    { title: '数量', dataIndex: 'goods_number', key: 'num', width: 70 },
    { title: '实收', dataIndex: 'order_amount', key: 'amt', width: 100, render: (v) => yuan(v) },
    { title: '收货人', dataIndex: 'receive_name', key: 'receiver', width: 110, ellipsis: true },
    { title: '地址', dataIndex: 'consumerAddress', key: 'addr', ellipsis: true },
    { title: '快递单号', dataIndex: 'tracking_number', key: 'tracking', width: 150, ellipsis: true },
    { title: '状态', dataIndex: 'order_status_str', key: 'status', width: 100, render: (v) => v ? <Tag color="blue">{v}</Tag> : '-' },
  ];

  // 售后表格列
  const aftersaleColumns = [
    { title: '售后单号', dataIndex: 'after_sales_id', key: 'id', width: 150, ellipsis: true },
    { title: '订单号', dataIndex: 'order_sn', key: 'sn', width: 180, ellipsis: true },
    { title: '商品', dataIndex: 'goods_name', key: 'goods', ellipsis: true },
    { title: '类型', dataIndex: 'after_sales_type_str', key: 'type', width: 120 },
    { title: '状态', dataIndex: 'after_sales_status_str', key: 'status', width: 110 },
    { title: '金额', dataIndex: 'after_sales_amount', key: 'amt', width: 100, render: (v) => v != null ? yuan(v) : '-' },
  ];

  return (
    <div className="page-container" style={{ padding: 16 }}>
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 3, height: 15, borderRadius: 2, background: 'var(--brand)', display: 'inline-block' }} />
            店铺数据（采集器同步）
          </span>
          <Space wrap>
            <Select
              style={{ width: 220 }}
              placeholder="选择店铺"
              value={selectedShop}
              onChange={setSelectedShop}
              options={shops.map(s => ({ value: s, label: s }))}
              notFoundContent={shops.length === 0 ? '暂无店铺数据' : undefined}
            />
            <span style={{ color: '#8C8177', fontSize: 12 }}>
              数据由桌面「数据采集器」登录拼多多后同步上传
            </span>
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => loadData()}>刷新</Button>
          </Space>
        </div>
      </Card>

      {shops.length === 0 ? (
        <Card><Empty description="暂无采集数据。请在桌面「数据采集器」添加店铺、登录拼多多并点击同步" /></Card>
      ) : dims.length === 0 ? (
        <Card><Empty description="该店铺暂无采集数据，请在该店铺的「数据采集器」中同步" /></Card>
      ) : (
        <Row gutter={[12, 12]}>
          {/* 推广汇总 */}
          {current['推广'] && current['推广'].data && (() => {
            const p = current['推广'].data;
            return (
              <Col span={24}>
                <Card size="small" title={<Space><BarChartOutlined style={{ color: '#1677ff' }} />推广数据（汇总）<Tag color="green">{current['推广'].updated_at}</Tag></Space>}>
                  <Row gutter={16}>
                    <Col span={4}><Statistic title="总花费(元)" value={p.spend} precision={2} valueStyle={{ color: 'var(--brand)' }} /></Col>
                    <Col span={4}><Statistic title="成交花费(元)" value={p.orderSpend} precision={2} /></Col>
                    <Col span={4}><Statistic title="交易额(元)" value={p.gmv} precision={2} valueStyle={{ color: '#1677ff' }} /></Col>
                    <Col span={4}><Statistic title="净交易额(元)" value={p.netGmv} precision={2} /></Col>
                    <Col span={4}><Statistic title="投产比" value={p.roi} precision={2} valueStyle={{ color: '#52c41a' }} /></Col>
                    <Col span={4}><Statistic title="转化率(%)" value={p.cvr} precision={2} /></Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={4}><Statistic title="点击率(%)" value={p.ctr} precision={2} /></Col>
                    <Col span={4}><Statistic title="平均支付金额(元)" value={p.avgPayAmount} precision={2} /></Col>
                  </Row>
                </Card>
              </Col>
            );
          })()}

          {/* 商品 */}
          {current['商品'] && current['商品'].data && (
            <Col span={24}>
              <Card size="small" title={<Space><ShoppingCartOutlined style={{ color: 'var(--brand)' }} />商品数据 <Tag color="blue">{current['商品'].data.list ? current['商品'].data.list.length : current['商品'].data.total} 个</Tag><Tag color="green">{current['商品'].updated_at}</Tag></Space>}>
                <Table rowKey="goods_id" size="small" columns={goodsColumns} dataSource={current['商品'].data.list || []} pagination={{ pageSize: 10 }} />
              </Card>
            </Col>
          )}

          {/* 订单发货 */}
          {current['订单发货'] && current['订单发货'].data && (
            <Col span={24}>
              <Card size="small" title={<Space><ShopOutlined style={{ color: '#52c41a' }} />订单发货 <Tag color="blue">{current['订单发货'].data.list ? current['订单发货'].data.list.length : current['订单发货'].data.total} 单</Tag><Tag color="green">{current['订单发货'].updated_at}</Tag></Space>}>
                <Table rowKey="order_sn" size="small" columns={orderColumns} dataSource={current['订单发货'].data.list || []} pagination={{ pageSize: 10 }} scroll={{ x: 1200 }} />
              </Card>
            </Col>
          )}

          {/* 售后 */}
          {current['售后'] && current['售后'].data && (
            <Col span={24}>
              <Card size="small" title={<Space><CustomerServiceOutlined style={{ color: '#faad14' }} />售后数据 <Tag color="blue">{current['售后'].data.list ? current['售后'].data.list.length : 0} 条</Tag><Tag color="green">{current['售后'].updated_at}</Tag></Space>}>
                {current['售后'].data.list && current['售后'].data.list.length > 0
                  ? <Table rowKey="after_sales_id" size="small" columns={aftersaleColumns} dataSource={current['售后'].data.list} pagination={{ pageSize: 10 }} />
                  : <Empty description="当前无售后单" />}
              </Card>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
}
